import { compose, renderNothing, branch, withState, withProps, withHandlers, withPropsOnChange } from 'recompose';
import { connect } from 'react-redux';
import { reduxForm, formValueSelector } from 'redux-form';
import { withRouter } from 'react-router';
import Joi from 'joi-full';
import { pathOr, pick, over, defaultTo, lensPath, pipe, set } from 'ramda';
import { get } from 'lodash';
import { t } from '../../../../../modules/infra/i18n';

import withNotifications from '../../../../common/withNotifications';
import { prepareSchemasByCountry } from '../../../../common/addressForm/schema';
import { syncValidateData } from '../../../../common/validator';
import { COUNTRY_NAME_BY_CODE } from '../../../shops/constants';
import BrandSettings from '../components/BrandSettings';
import { updateBrandSettings } from '../actions';
import { checkEmptyAddress } from '../utils';
import getLanguageOptionsByCodes from '../../../../common/LanguageMultiSelect/getLanguageOptionsByCodes';
import { prepareLanguageSettings, preparePrimaryAccountOwner } from '../../../../admin/brands/helpers';
import { flagNames, withInjectFeatureFlagsToProps } from '../../../../../modules/infra/feature-flags';
import { canUseConversionTracking } from '../../../../common/helpers/users';

const BRAND_SETTINGS_FORM = 'brandSettings';

const baseSchema = {
  email: Joi.string().allow('', null).email({ minDomainAtoms: 2 }).label('Email')
    .required(),
};

const schemaByCountry = prepareSchemasByCountry(baseSchema);

const validate = (model) => {
  const address = model.address;
  const country = address ? address.country : COUNTRY_NAME_BY_CODE.US;
  return syncValidateData(model, schemaByCountry[country]);
};

const form = reduxForm({
  form: BRAND_SETTINGS_FORM,
  validate,
});

const formSelector = formValueSelector(BRAND_SETTINGS_FORM);

const BrandSettingsContainer = compose(
  withRouter,
  withNotifications,
  withState('busy', 'setBusy', false),
  connect(
    (state) => {
      const supportedLanguages = getLanguageOptionsByCodes(get(state, 'auth.user.brand.settings.supportedLanguages'));
      const defaultLanguage = supportedLanguages
        .find(({ value }) => value === get(state, 'auth.user.brand.settings.defaultLanguage'))
        || supportedLanguages[0];

      return ({
        initialValues: {
          ...state.auth.user.brand,
          settings: {
            ...state.auth.user.brand.settings,
            supportedLanguages,
            defaultLanguage,
          },
        },
        baseUrl: '/',
        formCountry: formSelector(state, 'address.country'),
        supportedLanguages: formSelector(state, 'settings.supportedLanguages'),
        defaultLanguage: formSelector(state, 'settings.defaultLanguage'),
        conversionTracking: canUseConversionTracking(state),
      });
    },
    ({
      updateBrandSettings,
    })
  ),
  branch(
    props => !props.initialValues,
    renderNothing,
  ),
  withProps(props => ({
    cancelHref: props.baseUrl,
    brandCountry: pathOr(null, ['address', 'country'], props.initialValues),
  })),
  withHandlers({
    onSubmit: props => (brand) => {
      props.setBusy(true);
      const updateData = pipe(
        pick(['address', 'email']),
        over(lensPath(['address', 'street2']), defaultTo('')),
        checkEmptyAddress,
        set(
          lensPath(['settings']),
          pick(['defaultLanguage', 'supportedLanguages', 'gaMeasurementId', 'metaPixelId'], brand.settings || {})
        ),
        prepareLanguageSettings,
        preparePrimaryAccountOwner,
      )(brand);

      props.updateBrandSettings(brand.id, updateData)
        .then(() => {
          props.setBusy(false);
          props.router.push(props.baseUrl);
          props.addNotification({
            title: t('settings:brand_settings.success_title'),
            level: 'success',
            message: t('settings:brand_settings.success_message'),
          });
        })
        .catch(() => {
          props.setBusy(false);
        });
    },
  }),
  form,
  withPropsOnChange(['formCountry'], (props) => {
    if (props.formCountry === '') {
      // clear hidden fields
      props.change('address.city', '');
      props.change('address.zip', '');
      props.change('address.state', '');
    }
  }),
  withInjectFeatureFlagsToProps([
    { name: flagNames.BRAND_LEVEL_META_PIXEL_MANAGEMENT, as: 'showMetaPixelManagement' },
    { name: flagNames.BRAND_LEVEL_ANALYTICS_MANAGEMENT, as: 'showAnalyticsManagement' },
  ]),
  withPropsOnChange(['supportedLanguages'], (props) => {
    if (!(props.supportedLanguages || []).includes(props.defaultLanguage)) {
      props.change('settings.defaultLanguage', (props.supportedLanguages || [])[0]);
    }
  }),
)(BrandSettings);

export default BrandSettingsContainer;
